import { Menu, Transition } from "@headlessui/react";
import { useLogout, useUser } from "@hooks/useAuth";
import { authService } from "@services/authService";
import { useAuthStore } from "@store/authStore";
import { useUIStore } from "@store/uiStore";
import { cn } from "@utils";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import "remixicon/fonts/remixicon.css";

export default function Header() {
  const navigate = useNavigate();
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);
  const { refreshToken } = useAuthStore();
  const { data: user } = useUser();
  const logout = useLogout();

  const initials = user?.firstName?.[0] || "U";

  const handleLogout = async () => {
    try {
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
    } catch {
      // Ignore logout API errors - proceed with local logout
    } finally {
      logout();
      navigate("/login");
      toast.success("Logout successful!");
    }
  };

  return (
    <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-earth-200 bg-white px-6">
      {/* Left side */}
      <div className="flex items-center gap-4">
        {/* Mobile menu button */}
        <button
          onClick={toggleSidebar}
          className="lg:hidden flex h-10 w-10 items-center justify-center rounded-lg hover:bg-earth-100 transition-colors"
        >
          <i className="ri-menu-line text-xl text-earth-600"></i>
        </button>

        {/* Breadcrumb / Date */}
        <div className="hidden sm:block">
          <p className="text-sm text-earth-500">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-3">
        {/* User menu */}
        <Menu as="div" className="relative">
          <Menu.Button className="flex items-center gap-3 rounded-lg p-2 hover:bg-earth-100 transition-colors">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-earth-600 text-white text-sm font-semibold">
              {initials}
            </div>
            <span className="hidden text-sm font-medium text-earth-900 lg:block">
              {user?.firstName || "Student"}
            </span>
            <i className="ri-arrow-down-s-line text-earth-400 hidden lg:block"></i>
          </Menu.Button>

          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-lg bg-white py-1 shadow-lg ring-1 ring-earth-200 focus:outline-none">
              <div className="px-4 py-3 border-b border-earth-100">
                <p className="text-sm font-semibold text-earth-900">
                  {user?.fullName || "Student"}
                </p>
                <p className="text-xs text-earth-500 truncate">
                  {user?.email || ""}
                </p>
              </div>

              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => navigate("/profile")}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-2.5 text-sm",
                      active ? "bg-earth-50 text-earth-900" : "text-earth-700",
                    )}
                  >
                    <i className="ri-user-line text-earth-500"></i>
                    My Profile
                  </button>
                )}
              </Menu.Item>

              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => navigate("/profile?tab=settings")}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-2.5 text-sm",
                      active ? "bg-earth-50 text-earth-900" : "text-earth-700",
                    )}
                  >
                    <i className="ri-settings-4-line text-earth-500"></i>
                    Settings
                  </button>
                )}
              </Menu.Item>

              <div className="border-t border-earth-100 my-1" />

              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleLogout}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-2.5 text-sm",
                      active ? "bg-error-50 text-error-700" : "text-error-600",
                    )}
                  >
                    <i className="ri-logout-box-r-line"></i>
                    Logout
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </header>
  );
}
